import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/common/Logo";
import { Mail, Globe, ArrowUpRight } from "lucide-react";

const APP_URL = (process.env.REACT_APP_APP_URL || "").replace(/\/$/, "");
const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const OWNER_REQUIRED_MARKER = "OWNER_REQUIRED";

/**
 * Renderizza la riga fiscale del footer leggendo l'identità aziendale
 * pubblica esposta da `GET /api/public/business-identity`. Se un campo
 * è ``OWNER_REQUIRED`` viene omesso dalla riga finale.
 */
function BusinessIdentityLine() {
  const [identity, setIdentity] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/public/business-identity`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) setIdentity(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const fallback = "P.IVA in attivazione · Sede legale: Italia";
  if (!identity) return <div data-testid="footer-business-identity">{fallback}</div>;

  const parts = [];
  if (identity.legal_business_name && identity.legal_business_name !== OWNER_REQUIRED_MARKER) {
    parts.push(identity.legal_business_name);
  }
  if (identity.vat_number && identity.vat_number !== OWNER_REQUIRED_MARKER) {
    parts.push(`P.IVA ${identity.vat_number}`);
  }
  const addressBits = [identity.legal_address, identity.legal_city, identity.legal_zip, identity.legal_province]
    .filter((v) => v && v !== OWNER_REQUIRED_MARKER);
  if (addressBits.length > 0) {
    parts.push(`Sede legale: ${addressBits.join(" · ")}`);
  }
  if (parts.length === 0) {
    return <div data-testid="footer-business-identity">{fallback}</div>;
  }
  return (
    <div data-testid="footer-business-identity">{parts.join(" · ")}</div>
  );
}

export default function PublicFooter() {
  return (
    <footer
      data-testid="public-footer"
      className="bg-[#0F172A] text-[#94A3B8] mt-auto"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand column */}
          <div className="md:col-span-4">
            <div className="bg-white inline-flex items-center rounded-lg px-3 py-2">
              <Logo variant="full" size="md" />
            </div>
            <p className="mt-5 text-sm leading-relaxed max-w-xs text-[#CBD5E1]">
              La piattaforma SaaS che trasforma costi confusi, sprechi e caos
              operativo in margini sani e decisioni intelligenti.
            </p>
            <p className="mt-4 text-xs italic text-[#0D9488]">
              Control costs. Maximize profit.
            </p>
          </div>

          {/* Prodotto */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-white font-semibold mb-4">
              Prodotto
            </h4>
            <ul className="space-y-2.5 text-sm">
              <FooterLink to="/restaurant">Restaurant</FooterLink>
              <FooterLink to="/hotel">Hotel</FooterLink>
              <FooterLink to="/combo">Combo</FooterLink>
              <FooterLink to="/enterprise">Enterprise</FooterLink>
              <FooterLink to="/features">Funzionalità</FooterLink>
              <FooterLink to="/pricing">Prezzi</FooterLink>
            </ul>
          </div>

          {/* Risorse */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-white font-semibold mb-4">
              Risorse
            </h4>
            <ul className="space-y-2.5 text-sm">
              <FooterLink to="/faq">FAQ</FooterLink>
              <FooterLink to="/contact">Contatti</FooterLink>
              <li>
                <a
                  href={`${APP_URL}/login`}
                  className="hover:text-white transition-colors inline-flex items-center gap-1.5 group"
                >
                  Accedi
                  <ArrowUpRight className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                </a>
              </li>
              <li>
                <a
                  href={`${APP_URL}/pricing`}
                  className="hover:text-white transition-colors inline-flex items-center gap-1.5 group"
                >
                  Inizia gratis
                  <ArrowUpRight className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                </a>
              </li>
            </ul>
          </div>

          {/* Legale */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-white font-semibold mb-4">
              Legale
            </h4>
            <ul className="space-y-2.5 text-sm">
              <FooterLink to="/terms">Terms & Conditions</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/cookies">Cookie Policy</FooterLink>
              <FooterLink to="/refund-policy">Refund Policy</FooterLink>
            </ul>
          </div>

          {/* Contatti */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-white font-semibold mb-4">
              Contatti
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li className="inline-flex items-start gap-2">
                <Mail className="w-3.5 h-3.5 text-[#0D9488] mt-1 flex-shrink-0" />
                <a
                  href="mailto:support@costcontrolx.com"
                  className="hover:text-white transition-colors"
                >
                  support@costcontrolx.com
                </a>
              </li>
              <li className="inline-flex items-start gap-2">
                <Mail className="w-3.5 h-3.5 text-[#0D9488] mt-1 flex-shrink-0" />
                <a
                  href="mailto:billing@costcontrolx.com"
                  className="hover:text-white transition-colors"
                >
                  billing@costcontrolx.com
                </a>
              </li>
              <li className="inline-flex items-start gap-2">
                <Globe className="w-3.5 h-3.5 text-[#0D9488] mt-1 flex-shrink-0" />
                <a
                  href="https://costcontrolx.app"
                  className="hover:text-white transition-colors"
                >
                  costcontrolx.app
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-14 pt-8 border-t border-white/10 grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
          {[
            "Privacy & GDPR-ready",
            "Stripe secured billing",
            "Audit log append-only",
            "Multi-tenant isolato",
            "Backup cloud",
          ].map((b) => (
            <div
              key={b}
              className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]"
            >
              {b}
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 items-center justify-between text-[11px] text-[#64748B]">
          <div>
            © {new Date().getFullYear()} CostControlX · Hospitality SaaS Suite.
            Tutti i diritti riservati.
          </div>
          <BusinessIdentityLine />
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link
        to={to}
        className="hover:text-white transition-colors"
        data-testid={`footer-link-${to.replace(/\//g, "")}`}
      >
        {children}
      </Link>
    </li>
  );
}
